import React from 'react';
import { Globe, Loader2, Monitor } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatChatTimestamp } from '@/lib/chatMessageMeta';

export default function BrowserQAPanel({
  steps = [],
  screenshot = null,
  url = '',
  active = false,
  running = false,
}) {
  const latest = steps[steps.length - 1];

  return (
    <div
      className="flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-cyan-500/20 bg-[#080808]"
      data-testid="app-builder-browser-qa-panel"
    >
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-white/[0.06] px-4 py-3">
        <div className="flex items-center gap-2">
          <Monitor className="h-4 w-4 text-cyan-400" />
          <div>
            <p className="font-['IBM_Plex_Sans',sans-serif] text-sm font-medium text-white">Agent browser</p>
            <p className="text-[11px] text-zinc-500">Autonomous QA — live verification view</p>
          </div>
        </div>
        {running || active ? (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-2.5 py-1 text-[10px] text-cyan-200">
            <Loader2 className="h-3 w-3 animate-spin" />
            Verifying
          </span>
        ) : (
          <span className="text-[10px] text-zinc-600">Idle</span>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {url ? (
          <div className="mb-3 flex items-start gap-2 rounded-xl border border-white/[0.06] bg-white/[0.03] px-3 py-2 text-[11px] text-zinc-400">
            <Globe className="mt-0.5 h-3.5 w-3.5 shrink-0 text-cyan-400" />
            <span className="break-all">{url}</span>
          </div>
        ) : null}

        <div
          className={cn(
            'relative overflow-hidden rounded-xl border border-white/[0.08] bg-black',
            screenshot ? 'aspect-[16/10]' : 'flex min-h-[220px] items-center justify-center',
          )}
          data-testid="app-builder-browser-screenshot"
        >
          {screenshot ? (
            <img
              src={`data:image/jpeg;base64,${screenshot}`}
              alt="Agent browser screenshot"
              className="h-full w-full object-cover object-top"
            />
          ) : (
            <p className="px-6 text-center text-xs text-zinc-500">
              {running
                ? 'Opening internal browser and capturing the preview…'
                : 'Browser QA screenshots appear here during autonomous verification.'}
            </p>
          )}
          {running && screenshot ? (
            <div className="absolute bottom-3 right-3 rounded-full border border-cyan-500/30 bg-black/70 px-2 py-1 text-[10px] text-cyan-200">
              Live capture
            </div>
          ) : null}
        </div>

        <div className="mt-4 space-y-2" data-testid="app-builder-browser-steps">
          <p className="font-['JetBrains_Mono',monospace] text-[10px] uppercase tracking-[0.14em] text-zinc-500">
            Verification steps
          </p>
          {steps.length ? (
            steps.map((step, idx) => (
              <div
                key={`${step.step}-${idx}`}
                className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-['JetBrains_Mono',monospace] text-[11px] text-cyan-300">{step.step}</span>
                  {step.timestamp ? (
                    <span className="text-[10px] text-zinc-600">{formatChatTimestamp(step.timestamp)}</span>
                  ) : null}
                </div>
                <p className="mt-1 text-xs leading-5 text-zinc-400">{step.detail}</p>
              </div>
            ))
          ) : (
            <p className="text-xs text-zinc-600">No browser steps yet.</p>
          )}
          {latest && !steps.length ? null : latest ? (
            <p className="text-[10px] text-zinc-600">Latest: {latest.step} — {latest.detail}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
